import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import Icon from '../../../components/AppIcon';

const PendingCasesBreakdown = ({ analysis, loading }) => {
  if (loading) {
    return (
      <div className="glass-card p-6 rounded-2xl">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-64 bg-gray-100 rounded"></div>
        </div>
      </div>
    );
  }

  const urgencyData = [
    { name: 'Routine', value: analysis?.byUrgency?.routine || 0, color: '#2563eb' },
    { name: 'Urgent', value: analysis?.byUrgency?.urgent || 0, color: '#f59e0b' },
    { name: 'Urgence', value: analysis?.byUrgency?.emergency || 0, color: '#ef4444' }
  ];

  const ageData = [
    { name: '< 24h', value: analysis?.byAge?.lessThan24h || 0, color: '#10b981' },
    { name: '24h - 3j', value: analysis?.byAge?.between24hAnd3d || 0, color: '#3b82f6' },
    { name: '3j - 7j', value: analysis?.byAge?.between3dAnd7d || 0, color: '#f59e0b' },
    { name: '> 7j', value: analysis?.byAge?.moreThan7d || 0, color: '#ef4444' }
  ];

  return (
    <div className="glass-card p-6 rounded-2xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-warning/10 flex items-center justify-center">
          <Icon name="AlertCircle" size={20} className="text-warning" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-foreground">Cas en attente</h3>
          <p className="text-sm text-muted-foreground">{analysis?.total || 0} cas à traiter</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* By Urgency */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3">Par urgence</h4>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie
                data={urgencyData}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={70}
                paddingAngle={5}
                dataKey="value"
              >
                {urgencyData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-2 mt-2">
            {urgencyData?.map((item, index) => (
              <div key={index} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item?.color }}></div>
                  <span className="text-muted-foreground">{item?.name}</span>
                </div>
                <span className="font-semibold text-foreground">{item?.value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* By Age */}
        <div>
          <h4 className="text-sm font-semibold text-foreground mb-3">Par ancienneté</h4>
          <ResponsiveContainer width="100%" height={180}>
            <PieChart>
              <Pie
                data={ageData}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={70}
                paddingAngle={5}
                dataKey="value"
              >
                {ageData?.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry?.color} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-2 mt-2">
            {ageData?.map((item, index) => (
              <div key={index} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item?.color }}></div>
                  <span className="text-muted-foreground">{item?.name}</span>
                </div>
                <span className="font-semibold text-foreground">{item?.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Status breakdown */}
      <div className="pt-6 border-t border-gray-200">
        <h4 className="text-sm font-semibold text-foreground mb-3">Par statut</h4>
        <div className="grid grid-cols-2 gap-3">
          <div className="p-3 bg-blue-50 rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">Nouveaux</p>
            <p className="text-2xl font-bold text-primary">{analysis?.byStatus?.new || 0}</p>
          </div>
          <div className="p-3 bg-yellow-50 rounded-lg">
            <p className="text-xs text-muted-foreground mb-1">En revue</p>
            <p className="text-2xl font-bold text-warning">{analysis?.byStatus?.reviewed || 0}</p>
          </div>
        </div>
      </div>
      {/* Oldest pending alert */}
      {analysis?.oldestPending && (
        <div className="mt-4 p-3 bg-error/10 border border-error/20 rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="AlertTriangle" size={16} className="text-error mt-0.5" />
            <div className="flex-1">
              <p className="text-xs font-semibold text-error mb-1">Cas le plus ancien</p>
              <p className="text-sm text-foreground">{analysis?.oldestPending?.patientName}</p>
              <p className="text-xs text-muted-foreground">
                En attente depuis {analysis?.oldestPending?.daysOld} jours • {analysis?.oldestPending?.urgency}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingCasesBreakdown;